<template>
  <default-layout v-if="$route.name !== 'partition-import'">
    <card-toolbar
      icon="vpn_key"
      :title="`${this.$tc('key', 2)}/${this.$tc('certificate', 2)}`"
    >
      <template #actions>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              icon
              v-on="on"
              :disabled="loading"
              id="reload"
              @click="loadKeys"
            >
              <v-icon outline>
                autorenew
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc("reload") }}</span>
        </v-tooltip>

        <selection-menu
          left
          nudge-right="40"
          :options="menuOptions"
          icon="add"
          :tooltip="$t('new_object_key')"
          parent-name="PartitionObjectsPage"
        />
      </template>
    </card-toolbar>

    <v-card flat class="rounded-lg mb-8" outlined style="background: transparent;">
      <v-row no-gutters class="pa-4">
        <v-text-field
          id="PartitionObjectsPage_search"
          append-icon="mdi-magnify"
          :label="$tc('search')"
          single-line
          hide-details
          clearable
          @input="inputDebounced"
          @click:clear="clearSearch"
        />
      </v-row>
      <objects-table
        :full-items="fullItems"
        :search="search"
        :loading="loading"
        @edit-obj="editKey"
        @csr="openCSRModal"
        @get-addr="redirectGetAddr"
        @derive="redirectDerive"
        @get-pbk="redirectGetPubKey"
        @export="exportKey"
        @split-secret="splitSecret"
        @match-secret="matchRecoverSecret"
        @recover-secret="matchRecoverSecret"
        @delete="askToDeleteKey"
      />
    </v-card>

    <confirm-modal
      id="keysDeleteModal"
      :open="showDeleteKeyModal"
      :title="`${$tc('remove_obj')} ?`"
      :message="removeMessage"
      :critical="isCriticalRemove"
      :body-subject="selectedItem"
      @negative="closeDeleteKeyModal"
      @positive="deleteKey"
    />
  </default-layout>
  <div v-else>
    <router-view />
  </div>
</template>

<script>
import ObjectsTable from './ObjectsTable.vue'
import PermissionsMixin from '@/mixins/permissions'
import ObjectUIMixin from '@/mixins/objectUI'
import CardToolbar from '@/components/CardToolbar'
import { SelectionMenu } from '@dinamonetworks/gui-comps-lib'
import ConfirmModal from '@/components/ConfirmModal.vue'
import debounce from 'lodash.debounce'
import DefaultLayout from '@/layouts/DefaultLayout.vue'

export default {
  components: {
    ConfirmModal,
    CardToolbar,
    SelectionMenu,
    ObjectsTable,
    DefaultLayout
  },
  mixins: [PermissionsMixin, ObjectUIMixin],
  created () {
    this.loadKeys()

    this.inputDebounced = debounce(event => {
      this.search = event
    }, 500)
  },
  beforeUnmount () {
    this.inputDebounced.cancel()
  },
  data () {
    return {
      time: null,
      showDeleteKeyModal: false,
      showExportKeyModal: false,
      fullItems: [],
      selectedItem: null,
      loading: true,
      search: null,
      isCriticalRemove: false,
      removeMessage: null
    }
  },
  computed: {
    menuOptions () {
      const self = this
      return [
        {
          title: this.$tc('create', 2),
          id: 'PartitionObjectsPage_create',
          action: () => {
            self.createKey({ name: null })
          }
        },
        {
          title: this.$tc('do_import'),
          id: 'PartitionObjectsPage_import',
          action: () => {
            self.doImport({ name: null })
          }
        }
      ]
    }
  },
  methods: {
    clearSearch () {
      this.search = null
    },
    openCSRModal (name) {
      this.$router.push({ name: 'csr', params: { name } })
    },
    redirectGetAddr (item, page) {
      const hsmID = item.partition
        ? item.partition + '/' + item.name
        : item.name

      this.$router.push(
        {
          name: 'get-addr',
          params: {
            name: hsmID,
            svg: this.getKeyIcon(item),
            iconcolor: this.getKeyIconColor(item.info.type, item.blocked),
            info: item.info,
            page
          }
        }
      )
    },
    redirectDerive (item, page) {
      const hsmID = item.partition
        ? item.partition + '/' + item.name
        : item.name

      this.$router.push(
        {
          name: 'derive',
          params: {
            name: hsmID,
            svg: this.getKeyIcon(item),
            iconcolor: this.getKeyIconColor(item.info.type, item.blocked),
            info: item.info,
            page
          }
        }
      )
    },
    redirectGetPubKey (item, page) {
      const hsmID = item.partition
        ? item.partition + '/' + item.name
        : item.name

      const firstOption = 'Blockchain Export'
      const keyImportOption = 1

      this.$router.push(
        {
          name: 'get-pbk',
          params: {
            thename: hsmID,
            svg: this.getKeyIcon(item),
            iconcolor: this.getKeyIconColor(item.info.type, item.blocked),
            info: item.info,
            keys: this.fullItems,
            blocked: item.blocked,
            firstOption,
            keyImportOption,
            page
          }
        }
      )
    },
    async  splitSecret (item, page) {
      const res = await this.$store.dispatch('hsm/skeep_probe_secret', { body: { obj: item } })

      const cspInfo = {
        csp: item,
        type: res.type,
        secLevel: res.sec_level
      }

      this.$router.push(
        {
          name: 'split-secret',
          params: {
            item: cspInfo,
            page
          }
        }
      )
    },
    async    matchRecoverSecret (item, recoverValue, page) {
      const res = await this.$store.dispatch('hsm/skeep_probe_secret', { body: { obj: item } })

      const cspInfo = {
        csp: item,
        type: res.type,
        secLevel: res.sec_level
      }

      this.$router.push(
        {
          name: 'match-recover-secret',
          params: {
            item: cspInfo,
            recoverValue,
            page
          }
        }
      )
    },
    loadKeys () {
      this.time = performance.now()
      this.loading = true
      this.$store.dispatch('keys/getListKeys').then(res => {
        this.fullItems = res
        this.loading = false
      })
    },
    // EDIT MODAL
    async editKey (item, page) {
      const hsmID = item.partition
        ? item.partition + '/' + item.name
        : item.name

      const itemSp80057 = await this.$store.dispatch('keys/getKeySP80057', { obj: hsmID })

      const itemP11 = await this.$store.dispatch('keys/getP11Manager', { obj: hsmID })

      this.$router.push(
        {
          name: 'edit-key',
          params: {
            name: hsmID,
            info: item.info,
            svg: this.getKeyIcon(item),
            iconcolor: this.getKeyIconColor(item.info.type, item.blocked),
            sp: itemSp80057,
            ponze: itemP11,
            data: item.data,
            page
          }
        }
      )
    },
    // IMPORT MODAL
    doImport () {
      this.$router.push({ name: 'import-keys' })
    },
    // CREATE MODAL
    createKey () {
      this.$router.push({ name: 'new-key', params: { keys: this.fullItems } })
    },
    // DELETE MODAL
    askToDeleteKey (name, element) {
      const type = element.type.type

      switch (type) {
        case 'x.509':
        case 'RSA PUB KEY':
        case 'ECC PUB KEY':
        case 'BLOB':
          this.isCriticalRemove = false
          this.removeMessage = this.$tc('confirm_key_delete')
          break

        default:
          this.isCriticalRemove = true
          this.removeMessage = this.$tc('warn_remove_bchain_key')
          break
      }
      this.showDeleteKeyModal = true
      this.selectedItem = name
    },
    closeDeleteKeyModal () {
      this.showDeleteKeyModal = false
      this.selectedItem = null
    },
    deleteKey () {
      this.$store
        .dispatch('keys/deleteKey', { name: this.selectedItem })
        .then(() => {
          this.$emit('success')
          this.loadKeys()
          this.showDeleteKeyModal = false
          this.selectedItem = null
        })
        .catch(err => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          this.$notify.hsmError(errObj, err)
        })
    },
    exportKey (item, page) {
      const hsmID = item.partition
        ? item.partition + '/' + item.name
        : item.name

      this.$router.push(
        {
          name: 'export-keys',
          params: {
            thename: hsmID,
            svg: this.getKeyIcon(item),
            iconcolor: this.getKeyIconColor(item.info.type, item.blocked),
            info: item.info,
            keys: this.fullItems,
            blocked: item.blocked,
            page
          }
        })
    }
  }
}
</script>

<style></style>
